import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, Navigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  WalletMinimal,
  TrendingUp,
  UserPlus,
} from "lucide-react";
import API from "../services/api";
import { useToast } from "../components/Toast";
import {
  AUTH_LIMITS,
  hasValidEmailFormat,
  normalizeEmail,
  sanitizeAuthField,
} from "../utils/authValidation";
import "./Auth.css";

export default function Login() {
  const toast = useToast();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const signupRedirectEmail = location.state?.email || "";
  const queryEmail = searchParams.get("email") || "";
  const signupRedirectToast = location.state?.toast || location.state?.feedback || null;
  const hasSignupRedirectState = Boolean(signupRedirectEmail || signupRedirectToast);
  const [email, setEmail] = useState(sanitizeAuthField("email", signupRedirectEmail || queryEmail));
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [xLoading, setXLoading] = useState(false);
  const navigate = useNavigate();
  const handledSocialCodeRef = useRef("");

  useEffect(() => {
    if (!hasSignupRedirectState) return;
    if (signupRedirectToast?.message) {
      const showToast = toast[signupRedirectToast.type] || toast.info;
      showToast(signupRedirectToast.message);
    }
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null },
    );
  }, [
    hasSignupRedirectState,
    location.pathname,
    location.search,
    navigate,
    signupRedirectToast,
    toast,
  ]);

  useEffect(() => {
    if (!signupRedirectEmail && queryEmail) {
      setEmail(sanitizeAuthField("email", queryEmail));
    }
  }, [queryEmail, signupRedirectEmail]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const provider = params.get("provider");
    const code = params.get("code");

    if (!["google", "x"].includes(String(provider)) || !code) return;
    const dedupeKey = `${provider}:${code}`;
    if (handledSocialCodeRef.current === dedupeKey) return;
    handledSocialCodeRef.current = dedupeKey;

    const isGoogle = provider === "google";
    if (isGoogle) {
      setGoogleLoading(true);
    } else {
      setXLoading(true);
    }

    const clearSocialQuery = () => {
      const nextParams = new URLSearchParams(location.search);
      nextParams.delete("provider");
      nextParams.delete("code");
      const nextSearch = nextParams.toString();
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, { replace: true });
    };

    API.get(`/auth/${provider}/exchange`, { params: { code } })
      .then((res) => {
        if (res.data?.token) {
          localStorage.setItem("token", res.data.token);
          navigate("/dashboard", { replace: true });
          return;
        }
        toast.error(`${isGoogle ? "Google" : "X"} sign-in failed`);
        clearSocialQuery();
      })
      .catch((err) => {
        const data = err?.response?.data;
        toast.error(data?.msg || `${isGoogle ? "Google" : "X"} sign-in failed`);
        clearSocialQuery();
      })
      .finally(() => {
        if (isGoogle) {
          setGoogleLoading(false);
        } else {
          setXLoading(false);
        }
      });
  }, [location.pathname, location.search, navigate, toast]);

  if (localStorage.getItem("token")) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalizedEmail = normalizeEmail(email);

    if (!normalizedEmail) {
      toast.error("Please enter your email.");
      return;
    }

    if (!hasValidEmailFormat(normalizedEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!password) {
      toast.error("Please enter your password.");
      return;
    }

    setLoading(true);
    try {
      const res = await API.post("/auth/login", {
        email: normalizedEmail,
        password,
      });
      localStorage.setItem("token", res.data.token);
      navigate("/dashboard");
    } catch (err) {
      const message = err?.response?.data?.msg || err?.message || "Login failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleXLogin = () => {
    if (!API.defaults.baseURL) {
      toast.error("X sign-in is not available right now.");
      return;
    }

    setXLoading(true);
    window.location.assign(`${API.defaults.baseURL}/auth/x`);
  };

  const handleGoogleLogin = () => {
    if (!API.defaults.baseURL) {
      toast.error("Google sign-in is not available right now.");
      return;
    }

    setGoogleLoading(true);
    window.location.assign(`${API.defaults.baseURL}/auth/google`);
  };

  return (
    <div className="auth-container login-page">
      <div className="auth-shell">
        <aside className="auth-aside">
          <div className="auth-aside-copy">
            <div className="auth-kicker">
              <WalletMinimal size={16} />
              Premix AI
            </div>
            <h1>Arbitrage intelligence for forex traders.</h1>
            <p>Spot, futures, execution, and market behavior for precious metals in one system.</p>

            <div className="auth-visual" aria-hidden="true">
              <lottie-player
                src="/trading.json"
                background="transparent"
                speed="1"
                loop
                autoplay
                className="auth-lottie"
              ></lottie-player>
            </div>
          </div>
        </aside>

        <section className="auth-panel">
          <div className="auth-panel-content">
            <div className="auth-mobile-mark" aria-hidden="true">
              <span className="auth-brand-mark">
                <TrendingUp size={24} />
              </span>
            </div>

            <div className="auth-header">
              <h2>Welcome Back</h2>
              <p>Sign in to continue to Premium Profit.</p>
            </div>

            <div className="google-signin-section">
              <div className="auth-provider-grid">
                <button
                  type="button"
                  className="google-auth-button"
                  disabled={googleLoading || xLoading || loading}
                  onClick={handleGoogleLogin}
                >
                  {googleLoading ? (
                    <div className="auth-loading-spinner auth-loading-spinner-social google-spinner"></div>
                  ) : (
                    <>
                      <svg
                        className="google-icon"
                        viewBox="-3 0 262 262"
                        xmlns="http://www.w3.org/2000/svg"
                        preserveAspectRatio="xMidYMid"
                        aria-hidden="true"
                      >
                        <path
                          d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
                          fill="currentColor"
                        />
                        <path
                          d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
                          fill="currentColor"
                        />
                        <path
                          d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
                          fill="currentColor"
                        />
                        <path
                          d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
                          fill="currentColor"
                        />
                      </svg>
                      Google
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="google-auth-button x-auth-button"
                  disabled={xLoading || googleLoading || loading}
                  onClick={handleXLogin}
                >
                  {xLoading ? (
                    <div className="auth-loading-spinner auth-loading-spinner-social x-spinner"></div>
                  ) : (
                    <>
                      <svg
                        className="x-icon"
                        viewBox="0 0 300 271"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          fill="currentColor"
                          d="m236 0h46l-101 115 118 156h-92.6l-72.5-94.8-83 94.8h-46l107-123-113-148h94.9l65.5 86.6zm-16.1 244h25.5l-165-218h-27.4z"
                        />
                      </svg>
                      <span className="x-auth-label">
                        <span>Twitter</span>
                        <span className="x-auth-slash" aria-hidden="true">
                          /
                        </span>
                        <span>X</span>
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <div className="input-wrapper">
                  <Mail className="input-icon" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(sanitizeAuthField("email", e.target.value))}
                    placeholder="Enter your email"
                    required
                    maxLength={AUTH_LIMITS.email}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="form-group-row">
                  <label htmlFor="password">Password</label>
                  <Link to="/forgot-password" className="forgot-link">
                    Forgot your password?
                  </Link>
                </div>
                <div className="input-wrapper">
                  <Lock className="input-icon" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(sanitizeAuthField("password", e.target.value))}
                    placeholder="Enter your password"
                    required
                    maxLength={AUTH_LIMITS.password}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button type="submit" className="auth-button" disabled={loading}>
                {loading ? (
                  <>
                    <div className="auth-loading-spinner"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            <div className="auth-links">
              <div className="auth-divider">
                <span>Don't have an account?</span>
              </div>

              <Link to="/signup" className="signup-link">
                <UserPlus size={17} />
                Create Account
              </Link>

              <div className="auth-legal-links">
                <Link to="/privacy-policy">Privacy Policy</Link>
                <span className="auth-legal-dot">&middot;</span>
                <Link to="/terms-of-service">Terms of Service</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
